import {
  COUNTRIES,
  LANGUAGES,
  USER,
  USERS,
} from "../shared/tagTypes";
import { baseApi } from "./base";

const userApi = baseApi
  .enhanceEndpoints({
    addTagTypes: [USERS, USER, COUNTRIES, LANGUAGES],
  })
  .injectEndpoints({
    endpoints: (builder) => ({
      getUsers: builder.query({
        query: (payload) => ({
          url: "/users",
          params: {
            ...(payload.role && { role: payload.role }),
            ...(payload.country && { country: payload.country }),
            ...(payload.language && {
              language: payload.language,
            }),
            ...(payload.includeDisabled && {
              includeDisabled: payload.includeDisabled,
            }),
          },
        }),
        providesTags: [USERS],
      }),
      getUser: builder.query({
        query: (payload) => ({
          url: `/users/${payload.id}`,
        }),
        providesTags: [USER],
      }),
      createUser: builder.mutation({
        query: (payload) => ({
          url: "/users",
          method: "POST",
          body: payload,
        }),
        invalidatesTags: [USERS],
      }),
      updateUser: builder.mutation({
        query: (payload) => ({
          url: `/users/${payload.id}`,
          method: "PUT",
          body: payload.body,
        }),
        invalidatesTags: [USERS],
      }),
      deleteUser: builder.mutation({
        query: (payload) => ({
          url: `/users/${payload.id}`,
          method: "DELETE",
        }),
      }),
      uploadUserPhoto: builder.mutation({
        query: (payload) => ({
          url: `/users/${payload.id}/photograph`,
          method: "POST",
          body: payload.body,
        }),
      }),
      deleteUserPhoto: builder.mutation({
        query: (payload) => ({
          url: `/users/${payload.id}/photograph`,
          method: "DELETE",
        }),
      }),
      getUserCountries: builder.query({
        query: () => ({
          url: "/users/countries",
        }),
        providesTags: [USERS],
      }),
      getCountries: builder.query({
        query: () => ({
          url: "/apps/countries",
        }),
        providesTags: [COUNTRIES],
      }),
      getLanguages: builder.query({
        query: () => ({
          url: "/apps/languages",
        }),
        providesTags: [LANGUAGES],
      }),
      getUserDevices: builder.query({
        query: (payload) => ({
          url: `/users/${payload.id}/devices`,
        }),
        providesTags: [USER],
      }),
      getUserSubmissions: builder.query({
        query: (payload) => ({
          url: `/users/${payload.id}/submissions?pageSize=${payload.pageSize}&submittedBefore=${payload.submittedBefore}`,
        }),
        providesTags: [USER],
      }),
      getUserApplicationSubmissions: builder.query({
        query: (payload) => ({
          url: `/users/${payload.id}/application-submissions`,
          params: {
            ...(payload.pageSize && {
              pageSize: payload.pageSize,
            }),
            ...(payload.submittedBefore && {
              submittedBefore: payload.submittedBefore,
            }),
            ...(payload.language && {
              language: payload.language,
            }),
          }
        }),
        providesTags: [USER],
      }),
      getUserActivities: builder.query({
        query: (payload) => ({
          url: `/users/${payload.id}/activities?pageSize=${payload.pageSize}&dateBefore=${payload.dateBefore}`,
        }),
        providesTags: [USER],
      }),
      updateBasicInfo: builder.mutation({
        query: (payload) => ({
          url: `/users/${payload.id}/basic-info`,
          method: "PATCH",
          body: payload.body,
        }),
        invalidatesTags: [USER],
      }),
      updateAccountInfo: builder.mutation({
        query: (payload) => ({
          url: `/users/${payload.id}/account-info`,
          method: "PATCH",
          body: payload.body,
        }),
        invalidatesTags: [USER],
      }),
      ResetPassword: builder.mutation({
        query: (payload) => ({
          url: `/users/${payload.id}/reset-password`,
          method: "POST",
          body: payload.body,
        }),
        invalidatesTags: [USER],
      }),
      updateContactInfo: builder.mutation({
        query: (payload) => ({
          url: `/users/${payload.id}/contact-info`,
          method: "PATCH",
          body: payload.body,
        }),
        invalidatesTags: [USER],
      }),
      updateWorkEducation: builder.mutation({
        query: (payload) => ({
          url: `/users/${payload.id}/work-education`,
          method: "PATCH",
          body: payload.body,
        }),
        invalidatesTags: [USER],
      }),
      updateReligiousInfo: builder.mutation({
        query: (payload) => ({
          url: `/users/${payload.id}/religious-info`,
          method: "PATCH",
          body: payload.body,
        }),
        invalidatesTags: [USER],
      }),
      updatePersonalInfo: builder.mutation({
        query: (payload) => ({
          url: `/users/${payload.id}/personal-info`,
          method: "PATCH",
          body: payload.body,
        }),
        invalidatesTags: [USER],
      }),
      updateProfilePhoto: builder.mutation({
        query: (payload) => ({
          url: `/users/${payload.id}/photograph`,
          method: "POST",
          body: payload.file,
        }),
        invalidatesTags: [USER],
      }),
      deleteProfilePhoto: builder.mutation({
        query: (userId) => ({
          url: `/users/${userId}/photograph`,
          method: "DELETE",
        }),
        invalidatesTags: [USER],
      }),
      sendPushNotification: builder.mutation({
        query: (payload) => ({
          url: "/users/send-push-notification",
          method: "POST",
          body: payload.body,
        }),
        invalidatesTags: [],
      }),
    }),
    overrideExisting: false,
  });

export const {
  useGetUsersQuery,
  useLazyGetUsersQuery,
  useGetUserQuery,
  useLazyGetUserQuery,
  useCreateUserMutation,
  useUpdateUserMutation,
  useDeleteUserMutation,
  useUploadUserPhotoMutation,
  useDeleteUserPhotoMutation,
  useGetUserCountriesQuery,
  useLazyGetUserCountriesQuery,
  useGetCountriesQuery,
  useLazyGetCountriesQuery,
  useGetLanguagesQuery,
  useLazyGetLanguagesQuery,
  useGetUserDevicesQuery,
  useLazyGetUserDevicesQuery,
  useGetUserSubmissionsQuery,
  useLazyGetUserSubmissionsQuery,
  useGetUserApplicationSubmissionsQuery,
  useLazyGetUserApplicationSubmissionsQuery,
  useGetUserActivitiesQuery,
  useLazyGetUserActivitiesQuery,
  useUpdateBasicInfoMutation,
  useUpdateAccountInfoMutation,
  useResetPasswordMutation,
  useUpdateContactInfoMutation,
  useUpdatePersonalInfoMutation,
  useUpdateReligiousInfoMutation,
  useUpdateWorkEducationMutation,
  useUpdateProfilePhotoMutation,
  useDeleteProfilePhotoMutation,
  useSendPushNotificationMutation
} = userApi;
